<div class="detail-page pt-2 pb-4">
  <div class="container-fluid px-2">
    <!-- Loading state -->
    @if (isLoading) {
      <app-assessment-detail-skeleton></app-assessment-detail-skeleton>
    }

    <!-- Error state -->
    @if (loadError) {
    <div class="alert alert-danger" role="alert">
      <strong>Error:</strong> {{ loadError }}
      <button class="btn btn-link" (click)="backToApplicationPortfolio()">← Back to Application Portfolio</button>
    </div>
    }
    <!-- Content -->
    @if (!isLoading && !loadError && application) {
    <div class="detail-content">
      <!-- Header with App ID and Action Buttons -->
      <div class="mb-3">
        <div class="d-flex justify-content-between align-items-center">
          <div class="app-id-name">
            <button class="btn btn-sm btn-action btn-back" (click)="backToApplicationPortfolio()">Back</button>
          </div>
          <div class="action-buttons d-flex gap-2">
            @if (hasRunningJob) {

            <div class="running-assessment-btn-indicator">
              <span class="spinner-border spinner-border-sm running-spinner" role="status" aria-hidden="true"></span>
              <span class="running-btn-text">{{ isAnyJobRateLimited ? 'Assessment waiting for GitHub' : 'AI Assessment Running...' }}</span>
            </div>

            } @else {

            <button class="button small" [matMenuTriggerFor]="runCodeMenu"
              [disabled]="isActionProcessing || (isEnterpriseLead && !isProjectLead)">
              Run Code...
            </button>
            <mat-menu #runCodeMenu="matMenu">
              <span
                [matTooltip]="runLightweightValidationDisabledReason"
                [matTooltipDisabled]="!runLightweightValidationDisabledReason"
                matTooltipPosition="right">
                <button mat-menu-item (click)="onRunLightweightValidation()"
                  [disabled]="isActionProcessing || !canRunLightweightValidation || (isEnterpriseLead && !isProjectLead)">
                  Run Lightweight Code Validation
                </button>
              </span>
              <span
                [matTooltip]="runCodeAssessmentDisabledReason"
                [matTooltipDisabled]="!runCodeAssessmentDisabledReason"
                matTooltipPosition="right">
                <button mat-menu-item (click)="onRunCodeAssessment()"
                  [disabled]="isActionProcessing || !canRunCodeAssessment || (isEnterpriseLead && !isProjectLead)">
                  Run Code Assessment
                </button>
              </span>
            </mat-menu>
            }

            <button class="button small" (click)="onActionClick()" [disabled]="isActionProcessing || (isEnterpriseLead && !isProjectLead)">Action</button>
            <button class="button small" (click)="onViewHistory()" [disabled]="isActionProcessing || isHistoryLoading">View History</button>
            <button class="button small" (click)="onDownloadPdfReport()" [disabled]="isActionProcessing || isDownloadingReport">
              {{ isDownloadingReport ? 'Generating...' : 'Download Report' }}
            </button>
          </div>
        </div>
      </div>

      @if (isAnyJobRateLimited) {
      <div class="alert alert-warning assessment-rate-limit-banner mb-3" role="status" aria-live="polite" aria-atomic="true">
        <strong>Assessment delayed because GitHub is rate-limiting requests.</strong>
        <div class="mt-1">
          @for (job of rateLimitedJobs; track job.id) {
          <div class="rate-limit-detail text-break">{{ getRateLimitMessageFor(job) }}</div>
          }
        </div>
      </div>
      }

      <!-- History note -->
      @if (historyNote) {
      <p class="history-note-message">
        {{ historyNote }}
        @if (application.adoWorkItemUrl) {
          <a class="history-note-link" [href]="application.adoWorkItemUrl" target="_blank" rel="noopener noreferrer">
            ADO Feature #{{ application.adoWorkItemId }} ↗
          </a>
        }
      </p>
      }

      <!-- Newer assessment available banner -->
      @if (hasNewerAssessment && !hasRunningJob) {
      <div class="newer-assessment-banner mb-3">
        <span class="newer-assessment-icon">&#9888;</span>
        <span>A newer AI assessment has finished.</span>
        <button class="newer-assessment-link" (click)="onViewLatestAssessment()">View Latest</button>
      </div>
      }

      <!-- Application Information (As-Is) -->
      <div class="app-info-section mb-3">
        <div class="analysis-header">
          <div class="d-flex justify-content-between align-items-center">
            <div class="text-center flex-grow-1">Application Information (As-Is)</div>
            <small style="visibility: hidden;">placeholder</small>
          </div>
        </div>
        <div class="app-info-grid">
          <div class="app-info-row">
            <div class="app-info-cell">
              <div class="info-label">App ID</div>
              <div class="info-value">{{ application.appId || '-' }}</div>
            </div>
            <div class="app-info-cell">
              <div class="info-label">Digital Platform</div>
              <div class="info-value">{{ application.digitalPlatform || '-' }}</div>
            </div>
            <div class="app-info-cell">
              <div class="info-label">Application Management Style</div>
              <div class="info-value">{{ application.applicationManagementStyle }}</div>
            </div>
          </div>
          <div class="app-info-row">
            <div class="app-info-cell">
              <div class="info-label">App Name</div>
              <div class="info-value">{{ application.name || '-' }}</div>
            </div>
            <div class="app-info-cell">
              <div class="info-label">Product Line</div>
              <div class="info-value">{{ application.productLine || '-' }}</div>
            </div>
            <div class="app-info-cell">
              <div class="info-label">Planned Disposition</div>
              <div class="info-value">{{ application.plannedDisposition || '-' }}</div>
            </div>
          </div>
          <div class="app-info-row">
            <div class="app-info-cell">
              <div class="info-label">Business Criticality</div>
              <div class="info-value">{{ businessCriticalityDisplay }}</div>
            </div>
            <div class="app-info-cell">
              <div class="info-label">Business Capability</div>
              <div class="info-value">{{ application.primaryBusinessCapability || '-' }}</div>
            </div>

            <div class="app-info-cell">
              <div class="info-label">Business Owner / IT Application Owner</div>
              <div class="info-value">{{ application.businessOwner || '-' }} / {{ application.itApplicationOwner || '-' }}</div>
            </div>
          </div>
          <div class="app-info-row">
            <div class="app-info-cell">
              <div class="info-label">Users</div>
              <div class="info-value">{{ application.users != null ? (application.users | number:'1.0-0') : '-' }}</div>
            </div>
            <div class="app-info-cell">
              <div class="info-label">TCO Cost</div>
              <div class="info-value">{{ application.tcoCost != null ? '$' + (application.tcoCost | number:'1.0-0') :
                '-' }}</div>
            </div>
            <div class="app-info-cell">
              <div class="info-label">Incidents</div>
              <div class="info-value">{{ application.incidents != null ? (application.incidents | number:'1.0-0') : '-'
                }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Two Column Layout: CMDB Analysis & CMDB-versus-code validation summary -->
      <div class="row g-3 mb-3">
        <!-- AI-Powered CMDB Analysis -->
        <div class="col-12 col-lg-6">
          <div class="analysis-card cmdb-analysis">
            <div class="analysis-header cmdb-analysis-header" [class.cmdb-refresh-active]="isCmdbAnalysisRefreshQueued">
              <span>AI-Powered CMDB Analysis</span>
              <div class="cmdb-refresh-status" role="status">
                @if (isCmdbAnalysisRefreshQueued) {
                <span>Refreshing...</span>
                }
                <button mat-icon-button class="cmdb-refresh-button" type="button"
                  [matTooltip]="isCmdbAnalysisRefreshQueued ? 'CMDB analysis refresh is processing in the background' : 'Refresh CMDB analysis'"
                  [attr.aria-label]="isCmdbAnalysisRefreshQueued ? 'CMDB analysis refresh is processing in the background' : 'Refresh CMDB analysis'"
                  [disabled]="isCmdbAnalysisRefreshing || isCmdbAnalysisRefreshQueued"
                  (click)="confirmCmdbAnalysisRefresh()">
                  <mat-icon [class.cmdb-refresh-icon-spinning]="isCmdbAnalysisRefreshing">
                    {{ isCmdbAnalysisRefreshQueued ? 'hourglass_top' : 'refresh' }}
                  </mat-icon>
                </button>
              </div>
            </div>
            <div class="analysis-content">
              <div class="assessment-difference-note mb-3" role="note">
                <div>{{ cmdbPreliminaryText }}</div>
                @if (isCmdbOnlyRecommendation) {
                <div>{{ cmdbOnlyDisclaimerText }}</div>
                }
              </div>
            @if (isLowCmdbConfidence) {
              <div class="cmdb-confidence-note" role="note">
                <div><strong>Preliminary outcome:</strong> {{ preliminaryCmdbOutcome }}</div>
                <div><strong>Confidence:</strong> Low</div>
                <div><strong>Recommended next step:</strong> Run lightweight code validation.</div>
              </div>
            }
              <div class="dashed-divider"></div>
              <div class="analysis-row">
                <div class="analysis-item">
                  <div class="info-label">{{ cmdbOutcomeLabel }}</div>
                  <div class="info-value">{{ preliminaryCmdbOutcome }}</div>
                </div>
                <div class="analysis-item">
                  <div class="info-label">Key Reason</div>
                  <div class="info-value">{{ application.aiKeyReason || '-' }}</div>
                </div>
                <div class="analysis-item">
                  <div class="info-label">Recommendation</div>
                  <div class="info-value">{{ application.aiRecommendation || '-' }}</div>
                </div>
                <div class="analysis-item">
                  <div class="info-label">CMDB Confidence</div>
                  <div class="info-value">
                    <div class="confidence-popover-trigger" tabindex="0">
                      <span class="confidence-badge" [ngClass]="cmdbConfidenceClass">
                        {{ cmdbConfidenceLevel }}
                      </span>
                      <div class="confidence-popover" role="tooltip">
                        <div class="confidence-popover-title">CMDB Confidence: {{ cmdbConfidenceLevel }}</div>
                        <div class="confidence-popover-score">Score: {{ cmdbConfidenceScoreLabel }}</div>
                        @if (hasCmdbConfidenceSignals) {
                        <table class="confidence-signal-table">
                          <thead>
                            <tr>
                              <th>CMDB Signal</th>
                              <th>Status</th>
                              <th>Impact</th>
                            </tr>
                          </thead>
                          <tbody>
                            @for (signal of cmdbConfidenceSignals; track signal.name) {
                            <tr>
                              <td>{{ signal.name }}</td>
                              <td>{{ signal.isPositive ? 'Yes' : 'No / unknown' }}</td>
                              <td [class.signal-positive]="signal.isPositive" [class.signal-negative]="!signal.isPositive">
                                {{ signal.isPositive ? 'Positive' : 'Negative' }}
                              </td>
                            </tr>
                            }
                          </tbody>
                        </table>
                        } @else {
                        <div class="confidence-popover-empty">Signal details are not available.</div>
                        }
                      </div>
                    </div>
                  </div>
                </div>
                @if (hasCmdbAnalysisDate) {
                <div class="analysis-item">
                  <div class="info-label">Last Analysis Run</div>
                  <div class="info-value">{{ cmdbAnalyzedAt | utcDate:'date' }}</div>
                </div>
                }
              </div>
              <div class="rationale-section">
                <div class="rationale-label">Rationale</div>
                <div class="rationale-textbox">
                  {{ application.aiRationale || application.aiKeyReason || 'AI rationale not available.' }}
                </div>
                <a class="scoring-legend-link" (click)="openScoringLegend()" role="button" tabindex="0" (keydown.enter)="openScoringLegend()">
                  How is this scored? ↗
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Keep the summary beside CMDB analysis while its template remains near its related markup below. -->
        <ng-container *ngTemplateOutlet="assessmentSummary"></ng-container>

        <ng-template #codeAssessment>
        <!-- AI-Powered Code Assessment -->
        <div class="mb-3">
          <div class="analysis-card code-assessment">
            <div class="analysis-header" [ngClass]="{'analysis-header-failed': isFailed}">AI-Powered Code Assessment
            </div>

            <!-- Loading state -->
            @if (isJobLoading) {
            <div class="analysis-content code-assessment-loading">
              <span class="spinner-border spinner-border-sm me-2 text-secondary" role="status"
                aria-hidden="true"></span>
              <span class="text-muted" style="font-size:13px;">Loading assessment data...</span>
            </div>
            }

            <!-- Running state: assessment in progress and no previous result -->
            @if (!isJobLoading && hasRunningJob && !hasResult) {
            <div class="analysis-content code-assessment-running">
              <div class="running-state-indicator">
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                <span class="running-state-text">AI code assessment is running in the background...</span>
              </div>
              <p class="running-state-subtext">Results will appear here once the assessment completes.</p>
            </div>
            }

            <!-- Failed state -->
            @if (!isJobLoading && !(hasRunningJob && !hasResult) && isFailed) {
            <div class="analysis-content code-assessment-failed">
              <div class="d-flex align-items-start gap-2">
                <div class="flex-grow-1">
                  <div class="failed-inline-title">AI Assessment Failed</div>
                  <div class="failed-inline-message">
                    {{ job?.errorMessage || 'The assessment encountered an unexpected error. Please try running it
                    again.' }}
                  </div>
                  @if (job?.createdAt) {
                  <div class="failed-inline-meta text-muted">
                    Submitted: {{ job.createdAt | utcDate:'date' }}
                    @if (job?.completedAt) {
                    <span> &middot; Failed: {{ job.completedAt | utcDate:'date' }}</span>
                    }
                  </div>
                  }
                </div>
              </div>
            </div>
            }

            @if (isSourceCodeAssessmentNotAvailable) {
            <div class="analysis-content code-assessment-not-available">
              <div class="code-assessment-not-available-title">Source Code Assessment Not Available</div>
              <div class="code-assessment-not-available-message">
                {{ sourceCodeAssessmentNotAvailableMessage }}
              </div>
            </div>
            }

            <!-- Normal content -->
            @if (!isJobLoading && !(hasRunningJob && !hasResult) && !isFailed && !isSourceCodeAssessmentNotAvailable) {
            <div class="analysis-content">
              <div class="analysis-row">
                <div class="analysis-item">
                  <div class="info-label">Complexity Score</div>
                  <div class="info-value">{{ totalScore || job?.result?.totalScore || '-' }}</div>
                </div>
                <div class="analysis-item">
                  <div class="info-label">Size</div>
                  <div class="info-value">{{ job?.result?.summary.overallSize || '-' }}</div>
                </div>
                <div class="analysis-item">
                  <div class="info-label">Recommendation</div>
                  <div class="info-value">{{ job?.result?.summary?.recommendation || '-' }}</div>
                </div>
                @if (codeAssessmentRepositoryUrl) {
                <div class="analysis-item">
                  <div class="info-label">Repository</div>
                  <div class="info-value">
                    <a [href]="codeAssessmentRepositoryUrl" target="_blank" rel="noopener noreferrer" [title]="codeAssessmentRepositoryUrl">
                      {{ codeAssessmentRepositoryLabel }}
                    </a>
                  </div>
                </div>
                }
                @if (job?.branch) {
                <div class="analysis-item">
                  <div class="info-label">Branch</div>
                  <div class="info-value">{{ job?.branch }}</div>
                </div>
                }
                @if (job?.completedAt || job?.createdAt) {
                <div class="analysis-item">
                  <div class="info-label">AI Assessment Date</div>
                  <div class="info-value">{{ (job?.completedAt || job?.createdAt) | utcDate:'date' }}</div>
                </div>
                }
              </div>
              <div class="rationale-section">
                <div class="rationale-label">Rationale</div>
                <div class="rationale-textbox">
                  {{ job?.result?.summary?.rationale || job?.result?.summary?.keyReason || '-' }}
                </div>
              </div>
            </div>
            }
          </div>
        </div>
        </ng-template>
      </div>

      <!-- Full source-code assessment is intentionally separate from the CMDB validation overview. -->
      <ng-container *ngTemplateOutlet="codeAssessment"></ng-container>

      <ng-template #assessmentSummary>
      <div class="col-12 col-lg-6">
      <div class="assessment-summary-note h-100" role="status" aria-live="polite">
        <div class="assessment-summary-header analysis-header">
          <span>Lightweight Code Summary</span>
        </div>

        @if (showLightweightSummary) {
        <div class="assessment-summary-row">
          @if (lightweightValidations.length === 0) {
          <div class="lightweight-validation-message">{{ repositoryUnavailableMessage }}</div>
          }

          @if (lightweightValidations.length > 0) {
          <!-- Business-friendly summary, structured like the CMDB analysis card. -->
          @if (hasMultipleValidatedRepositories) {
          <div class="lightweight-multi-repo-note">
            Showing the finding with the highest legacy-risk score across {{ lightweightValidations.length }} repositories. See repository details below for a full breakdown.
          </div>
          <div class="dashed-divider"></div>
          }
          <div class="analysis-row lightweight-business-summary">
            <div class="analysis-item lightweight-hidden-column">
              <div class="info-label">Analysis Result</div>
              <div class="info-value">{{ lightweightAnalysisResultLabel }}</div>
            </div>
            <div class="analysis-item analysis-item-wide lightweight-hidden-column">
              <div class="info-label">Key Reason</div>
              <div class="info-value">{{ lightweightKeyReasonLabel }}</div>
            </div>
            <div class="analysis-item analysis-item-wide">
              <div class="info-label">Recommendation</div>
              <div class="info-value">{{ lightweightRecommendationLabel }}</div>
            </div>
            <div class="analysis-item">
              <div class="info-label">Assessment Confidence</div>
              <div class="info-value">
                <span class="confidence-badge" [ngClass]="lightweightConfidenceClass">
                  {{ lightweightConfidenceLabel }}
                </span>
              </div>
            </div>
            @if (lightweightRunDateLabel) {
            <div class="analysis-item">
              <div class="info-label">Run Date</div>
              <div class="info-value">{{ lightweightRunDateLabel | utcDate:'date' }}</div>
            </div>
            }
            <div class="analysis-item">
              <div class="info-label">Legacy Score</div>
              <div class="info-value">{{ primaryLightweightFinding?.score ?? '-' }}</div>
            </div>
          </div>
          }
        </div>
        }
           @if (!hasMultipleValidatedRepositories) {
           <div class="dashed-divider"></div>
           }
        <!-- Single consolidated rationale covering both CMDB-vs-code differences and the lightweight finding. -->
        <div class="rationale-section">
          <div class="rationale-label">Rationale</div>
          <div class="rationale-textbox">
            @if (isLowAssessmentCoverage) {
            <p class="rationale-line">{{ assessmentCoverageRecommendation }}</p>
            }

            @if (lightweightValidations.length > 0) {
            <p class="rationale-line">
              <strong>Lightweight code assessment finding:</strong> {{ lightweightRationaleLabel }}
            </p>
            }
          </div>
        </div>

        <!-- Collapsible repository-level details; shown after the rationale. -->
        @if (lightweightValidations.length > 0) {
        <div class="lightweight-repository-details">
          <button type="button" class="lightweight-details-toggle" (click)="toggleRepositoryDetailsSection()"
                  [attr.aria-expanded]="isRepositoryDetailsExpanded">
            {{ isRepositoryDetailsExpanded ? 'Hide repository details' : 'Show repository details' }}
          </button>

          @if (isRepositoryDetailsExpanded) {
          <div class="lightweight-repository-details-content">
            <div class="lightweight-repository-filter">
              <label class="info-label" for="lightweightRepositoryFilter">Repository</label>
              <select id="lightweightRepositoryFilter" class="lightweight-repository-select"
                      [value]="selectedLightweightRepositoryJobId"
                      (change)="onLightweightRepositoryFilterChange($any($event.target).value)">
                <option value="all">All Repositories</option>
                @for (option of lightweightRepositoryFilterOptions; track option.jobId) {
                <option [value]="option.jobId">{{ option.repositoryLabel }}</option>
                }
              </select>
            </div>

            <div class="lightweight-repository-table-wrapper">
              <table class="assessment-table lightweight-repository-table">
                <thead>
                  <tr>
                    <th>Repository</th>
                    <th>Recommendation</th>
                    <th>Score</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  @for (validation of filteredLightweightValidations; track validation.jobId) {
                  <tr>
                    <td>
                      @if (validation.repositoryUrl) {
                      <a [href]="validation.repositoryUrl" target="_blank" rel="noopener noreferrer" [title]="validation.repositoryUrl">
                        {{ validation.repositoryLabel }}
                      </a>
                      } @else {
                      {{ validation.repositoryLabel }}
                      }
                    </td>
                    <td>{{ validation.recommendedAction }}</td>
                    <td>{{ validation.score ?? '-' }}</td>
                    <td>
                      <button type="button" class="lightweight-details-toggle" (click)="toggleLightweightDetails(validation.jobId)"
                              [attr.aria-expanded]="isLightweightDetailsExpanded(validation.jobId)">
                        {{ isLightweightDetailsExpanded(validation.jobId) ? 'Hide details' : 'Show details' }}
                      </button>
                    </td>
                  </tr>
                  @if (isLightweightDetailsExpanded(validation.jobId)) {
                  <tr class="lightweight-repository-detail-row">
                    <td colspan="4">
                      <div class="lightweight-validation-message">{{ validation.message }}</div>
                      <div class="lightweight-validation-action">
                        <strong>Rationale:</strong>
                        <span> {{ validation.scoreExplanation || validation.message }}</span>
                      </div>
                      @if (validation.hasResult) {
                      <div class="lightweight-validation-meta">
                        <span class="lightweight-validation-pill"><strong>Branch:</strong> {{ validation.branch }}</span>
                        <span class="lightweight-validation-pill"><strong>Pipeline:</strong> {{ validation.pipelineMaturity }}</span>
                        <span class="lightweight-validation-pill"><strong>Technology signals:</strong> {{ validation.technologySignals }}</span>
                        @if (validation.legacyIndicatorsPreview) {
                        <span class="lightweight-validation-pill"><strong>Legacy indicators:</strong> {{ validation.legacyIndicatorsPreview }}</span>
                        }
                      </div>
                      }
                      <div class="lightweight-validation-action">
                        <strong>Legacy signals from lightweight code assessment:</strong>
                        @if (validation.signals.length === 0) {
                        <span> None detected.</span>
                        } @else {
                        <ul>
                          @for (signal of validation.signals; track signal.signal + signal.evidence) {
                          <li><strong>{{ signal.signal }} ({{ signal.severity }}):</strong> {{ signal.evidence }} — {{ signal.reason }}</li>
                          }
                        </ul>
                        }
                      </div>
                    </td>
                  </tr>
                  }
                  }
                </tbody>
              </table>
            </div>
          </div>
          }
        </div>
        }
      </div>
      </div>
      </ng-template>

      <!-- AI-Powered Code Assessment Details -->
      @if (hasResult) {
      <div class="assessment-details-section">
        <div class="analysis-header">AI-Powered Code Assessment Details</div>
        <div class="assessment-details-content">
          <div class="row g-4">
            <!-- Left: Details Table -->
            <div class="col-12 col-lg-7">
              @if (dimensionItems.length > 0) {
              <div class="details-table-wrapper">
                <table class="assessment-table">
                  <thead>
                    <tr>
                      <th>Dimension</th>
                      <!-- <th>Score</th> -->
                      <th>Size</th>
                      <th>Detailed Reasoning</th>
                      <th>Action Items</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (item of dimensionItems; track item) {
                    <tr>
                      <td class="dimension-name">{{ item.dimension }}</td>
                      <!-- <td class="dimension-score-cell">{{ item.score }}</td> -->
                      <td class="dimension-size-cell">
                        <span class="size-badge" [ngClass]="{
                          'size-small': item.size === 'Small',
                          'size-medium': item.size === 'Medium',
                          'size-large': item.size === 'Large'
                        }">{{ item.size }}</span>
                      </td>
                      <td class="reasoning-cell">
                        @if (item.isReasoningBulletList) {
                        <ul class="info-value">
                          @for (bullet of item.reasoningBullets; track bullet) {
                          <li>{{ bullet }}</li>
                          }
                        </ul>
                        } @else {
                        <div class="info-value">{{ item.reasoning }}</div>
                        }

                      </td>
                      <td class="action-items-cell">
                        @if (item.actionItems.length > 0) {
                        <ol class="action-items-list">
                          @for (action of item.actionItems; track action) {
                          <li>{{ action }}</li>
                          }
                        </ol>
                        } @else {
                        <div class="info-value text-muted">No action required</div>
                        }
                      </td>
                    </tr>
                    }
                  </tbody>
                </table>
              </div>
              }
            </div>

            <!-- Right: Spider Chart + Scope & Assumptions -->
            <div class="col-12 col-lg-5">
              <div class="chart-wrapper">
                <canvas #radarCanvas aria-label="Code Assessment radar chart" role="img"></canvas>
              </div>

              <!-- Recommended Modernization Scope -->
              @if (job?.result?.additionalDetails?.recommendedModernizationScope?.length) {
              <div class="scope-section mt-4">
                <div class="scope-title">Recommended Modernization Scope</div>
                <ol class="scope-list">
                  @for (scope of job!.result!.additionalDetails!.recommendedModernizationScope; track scope) {
                  <li>{{ scope }}
                  </li>
                  }
                </ol>
              </div>
              }

              <!-- Assumptions & Dependencies -->
              @if (job?.result?.additionalDetails?.assumptionsAndDependencies?.length) {
              <div class="assumptions-section mt-3">
                <div class="assumptions-title">Assumptions & Dependencies</div>
                <ol class="assumptions-list">
                  @for (assumption of job!.result!.additionalDetails!.assumptionsAndDependencies; track assumption) {
                  <li>{{
                    assumption }}</li>
                  }
                </ol>
              </div>
              }
            </div>
          </div>
        </div>
      </div>
      }


      <!-- Approval Logs -->
      @if (isArchitect && approvalLogs.length > 0) {
      <div class="card shadow-sm mt-4">
        <div class="card-body">
          <h6 class="section-title mb-3">
            <i class="bi bi-clock-history"></i> Approval History
          </h6>
          <div class="table-responsive">
            <table class="table table-sm table-hover">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Architect</th>
                  <th>Action</th>
                  <th>Original AI Recommendation</th>
                  <th>Final Decision</th>
                  <th>Override Reason</th>
                </tr>
              </thead>
              <tbody>
                @for (log of approvalLogs; track log) {
                <tr>
                  <td>{{ log.timestamp | utcDate:'date' }}</td>
                  <td>{{ log.architectName }}</td>
                  <td>
                    <span class="badge" [ngClass]="log.action === 'Accepted' ? 'bg-success' : 'bg-warning text-dark'">
                      {{ log.action }}
                    </span>
                  </td>
                  <td>{{ log.originalAiRecommendation }}</td>
                  <td>
                    <strong>{{ log.finalDecision }}</strong>
                  </td>
                  <td>
                    @if (!log.overrideReason) {
                    <span class="text-muted">—</span>
                    }
                    @if (log.overrideReason) {
                    <span>{{ log.overrideReason }}</span>
                    }
                  </td>
                </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
      }
    </div>
    }
  </div>
</div>
